import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { getTask, updateTask, completeTask, approveTask, reopenTask, addTaskComment, deleteTask } from '../utils/api';
import { Task, TaskReopen, TaskCommentCreate, TaskStatus, TaskPriority } from '../types/api';
import { getErrorMessage } from '../utils/api';

const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenFeedback, setReopenFeedback] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeRemarks, setCompleteRemarks] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  useEffect(() => {
    if (taskId) {
      loadTask();
    }
  }, [taskId]);

  const loadTask = async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      setError('');
      const data = await getTask(taskId);
      setTask(data);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load task'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!taskId || !newComment.trim()) return;

    try {
      setSubmitting(true);
      await addTaskComment(taskId, { comment: newComment });
      setNewComment('');
      await loadTask();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to add comment'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!taskId) return;

    try {
      setSubmitting(true);
      await completeTask(taskId, { remarks: completeRemarks });
      setShowCompleteModal(false);
      setCompleteRemarks('');
      await loadTask();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to complete task'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!taskId) return;

    try {
      setSubmitting(true);
      setError(''); // Clear any previous errors
      await approveTask(taskId);
      await loadTask();
      // After approval, task status will be 'approved'
      // User can navigate back to tasks list if needed
    } catch (err: any) {
      const errorMsg = getErrorMessage(err, 'Failed to approve task');
      setError(errorMsg);
      console.error('Error approving task:', err);
      // Reload task to ensure UI is in sync
      await loadTask();
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!taskId || !reopenFeedback.trim()) return;

    try {
      setSubmitting(true);
      await reopenTask(taskId, { feedback: reopenFeedback });
      setShowReopenModal(false);
      setReopenFeedback('');
      await loadTask();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to reopen task'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!taskId) return;

    try {
      setDeleting(true);
      await deleteTask(taskId);
      // Navigate back to tasks list after successful deletion
      navigate('/tasks');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to delete task'));
      setDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (!taskId) return;

    try {
      setSubmitting(true);
      await updateTask(taskId, { status: newStatus });
      await loadTask();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to update status'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'reopened':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isOverdue = () => {
    if (!task?.due_date || task.status === 'approved') return false;
    // Normalize both dates to local midnight for accurate comparison
    const dueDate = new Date(task.due_date);
    const dueDateLocal = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dueDateLocal < todayLocal;
  };

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  if (error && !task) {
    return (
      <div className="container-mobile py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <Link to="/tasks" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
            Back to Tasks
          </Link>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="container-mobile py-8">
        <p className="text-gray-600">Task not found</p>
        <Link to="/tasks" className="text-blue-600 hover:text-blue-800">Back to Tasks</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/tasks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Task Details</h1>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Link
              to={`/tasks/${taskId}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PencilIcon className="h-5 w-5" />
              Edit
            </Link>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Task Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{task.title}</h2>
            {task.description && (
              <p className="text-gray-600 mb-4">{task.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(task.priority)}`}>
              {task.priority.toUpperCase()}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.status)}`}>
              {task.status.replace('_', ' ').toUpperCase()}
            </span>
            {task.assignment_type === 'system' && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                SYSTEM
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Category:</span>
            <span className="ml-2 font-medium">{task.category.replace('_', ' ')}</span>
          </div>
          <div>
            <span className="text-gray-500">Assigned to:</span>
            <span className="ml-2 font-medium">{task.assigned_to_manager_name || 'Unknown'}</span>
          </div>
          <div>
            <span className="text-gray-500">Assigned by:</span>
            <span className="ml-2 font-medium">{task.assigned_by_name || 'System'}</span>
          </div>
          <div>
            <span className="text-gray-500">Due Date:</span>
            <span className={`ml-2 font-medium ${isOverdue() ? 'text-red-600' : ''}`}>
              {formatDate(task.due_date)}
              {isOverdue() && ' (Overdue)'}
            </span>
          </div>
          {task.completed_date && (
            <div>
              <span className="text-gray-500">Completed:</span>
              <span className="ml-2 font-medium">{formatDate(task.completed_date)}</span>
            </div>
          )}
          {task.approved_date && (
            <div>
              <span className="text-gray-500">Approved:</span>
              <span className="ml-2 font-medium">{formatDate(task.approved_date)}</span>
            </div>
          )}
        </div>

        {task.manager_remarks && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Manager Remarks:</p>
            <p className="text-sm text-gray-600">{task.manager_remarks}</p>
          </div>
        )}

        {task.admin_feedback && (
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Admin Feedback:</p>
            <p className="text-sm text-gray-600">{task.admin_feedback}</p>
          </div>
        )}

        {/* Reference Links */}
        {task.related_entity_type && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Related Information:</p>
            {task.system_rule_id === 'vaccination_recommendations' && (
              <Link
                to="/goat/vaccination-recommendations"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View Vaccination Recommendations →
              </Link>
            )}
            {task.system_rule_id === 'weight_recommendations' && (
              <Link
                to="/goat/weight-register"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View Weight Register →
              </Link>
            )}
            {task.system_rule_id === 'deworming_overdue' && (
              <Link
                to="/goat/health-compliance-report"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View Health Compliance Report →
              </Link>
            )}
            {task.system_rule_id === 'upcoming_kidding' && (
              <Link
                to="/goat/breeding-logs"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View Breeding Logs →
              </Link>
            )}
            {task.system_rule_id?.startsWith('missing_daily_logs') && (
              <Link
                to="/logs"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View Logs →
              </Link>
            )}
            {task.system_rule_id?.startsWith('upcoming_harvest') && (
              <Link
                to="/cycle-planning"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                View Cycle Planning →
              </Link>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
          {(isManager || isAdmin) && task.status === 'pending' && (
            <>
              {isManager && (
                <button
                  onClick={() => handleStatusChange('in_progress')}
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Mark In Progress
                </button>
              )}
              <button
                onClick={() => setShowCompleteModal(true)}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                Mark Complete
              </button>
            </>
          )}
          {(isManager || isAdmin) && task.status === 'in_progress' && (
            <button
              onClick={() => setShowCompleteModal(true)}
              disabled={submitting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              Mark Complete
            </button>
          )}
          {(isManager || isAdmin) && task.status === 'reopened' && (
            <button
              onClick={() => setShowCompleteModal(true)}
              disabled={submitting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              Mark Complete Again
            </button>
          )}
          {isAdmin && task.status === 'completed' && (
            <>
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => setShowReopenModal(true)}
                disabled={submitting}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                Reopen
              </button>
            </>
          )}
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Comments</h3>

        {/* Comments List */}
        <div className="space-y-4 mb-6">
          {task.comments.length === 0 ? (
            <p className="text-gray-500 text-sm">No comments yet</p>
          ) : (
            task.comments.map((comment) => (
              <div
                key={comment.id}
                className={`p-3 rounded-lg ${
                  comment.is_system ? 'bg-gray-50' : 'bg-blue-50'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {comment.commented_by_name || 'System'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatDate(comment.created_at)}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{comment.comment}</p>
              </div>
            ))
          )}
        </div>

        {/* Add Comment */}
        {(isAdmin || (isManager && task.assigned_to_manager_id === user?.id)) && (
          <div className="space-y-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleAddComment}
              disabled={!newComment.trim() || submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Comment'}
            </button>
          </div>
        )}
      </div>

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Complete Task</h3>
            <textarea
              value={completeRemarks}
              onChange={(e) => setCompleteRemarks(e.target.value)}
              placeholder="Add remarks (optional)..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={handleComplete}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Completing...' : 'Complete'}
              </button>
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setCompleteRemarks('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Modal */}
      {showReopenModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Reopen Task</h3>
            <textarea
              value={reopenFeedback}
              onChange={(e) => setReopenFeedback(e.target.value)}
              placeholder="Provide feedback for reopening..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              required
            />
            <div className="flex gap-2">
              <button
                onClick={handleReopen}
                disabled={!reopenFeedback.trim() || submitting}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Reopening...' : 'Reopen'}
              </button>
              <button
                onClick={() => {
                  setShowReopenModal(false);
                  setReopenFeedback('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-2 text-red-600">Delete Task</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this task? This action cannot be undone.
            </p>
            {task && (
              <p className="text-sm text-gray-500 mb-4 font-medium">
                Task: {task.title}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetailPage;

